import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { resolveDoseValue } from "../prescription/prescriptionDispenseUtils";
  import baseUrl from "../../../api/baseUrl";
import { RootState } from "../../../redux/store";
import {
  addAditionalNote,
  addAdvancedDiagnose,
  addDiagnoseNote,
  addDiagnoses,
  addDiagnosesImage,
  addIntraOralSoftTissues,
  setExtraOralExamination,
  setOcclusalAssessment,
  setBopChart,
  addInvestigations,
  addPatientComplains,
  addPastHistories,
  addPrescriptions,
  addTreatmentImage,
} from "../../../redux/diagnosestate/diagnoseAction";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../redux/loader/loaderActions";
import GenderButton from "../../../components/button/genderbtn/GenderButton";
import Investigation from "./components/investigation/Investigation";
import IntraOralSoftTissues from "./components/intraoralsofttissues/IntraOralSoftTissues";
import ExtraOralExamination from "./components/extraoralexamination/ExtraOralExamination";
import OcclusalAssessment from "./components/occlusalassessment/OcclusalAssessment";
import PatientComplain from "./components/patientComplain/PatientComplain";
import { toast } from "react-toastify";

import getDecryptedToken from "../../../api/getDecryptedToken";
import TeethCharts from "./components/teethdiagnosis/TeethCharts";
import BopChart from "./components/bop/BopChart";
import PastHistory from "./components/pasthistory/PastHistory";

// Lazy load components to improve initial load time
const Adult = React.lazy(() => import("./components/adult/Adult"));
const Child = React.lazy(() => import("./components/child/Child"));
const DiagnoseForm = React.lazy(
  () => import("./components/diagnoseform/DiagnoseForm")
);
const DiagnoseTable = React.lazy(
  () => import("./components/diagnosetable/DiagnoseTable")
);
const AllDiagnose = React.lazy(
  () => import("./components/alldiagnose/AllDiagnose")
);
const AddXray = React.lazy(
  () => import("../treatmenttable/components/addxray/AddXray")
);

interface ImageData {
  id: string;
  url: string;
  file: any;
  base64: any;
}

function Diagnose() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ID } = useParams();
  const [advancedChart, setAdvancedChart] = useState<boolean>(false);

  useEffect(() => {
    const advancedChartValue = getDecryptedToken("advancedChart");
    if (!advancedChartValue) {
      setAdvancedChart(false);
      return;
    }
    const normalizedValue = String(advancedChartValue).trim().toLowerCase();
    const isEnabled = normalizedValue === "true" || normalizedValue === "1" || normalizedValue === "yes";
    setAdvancedChart(isEnabled);
  }, []);

  const [makeAction, setmakeAction] = useState(false);
  const [adult, setadult] = useState(true);
  const [diagnoses, setdiagnoses] = useState({});
  const [formData, setFormData] = useState<ImageData[]>([]);

  const teeth = useSelector((state: RootState) => state.diagnose);
  const { makeaction } = useSelector((state: RootState) => state.diagnose);

  const getData = useCallback(() => {
    if (!ID) return;

    dispatch(setloadeActive());
    baseUrl
      .get("treatment/find-treatment", {
        headers: { treatmentid: ID },
      })
      .then((response) => {
        const {
          aditionalNote,
          patientComplain,
          diagnose,
          imageDiagnose,
          imageTreatment,
          complain,
          investigation,
          intraOralSoftTissues,
          extraOralExamination,
          occlusalAssessment,
          bopChart,
          prescription,
          advancedDiagnose,
          pastHistory,
        } = response.data.data;

        function cleanDoseArray(data: any) {
          return data.map((item: any) => ({
            _id: item?._id || "",
            dose: resolveDoseValue(item),
            doseId: item?.doseId || "",
            drugsId: item?.drugsId?._id || "",
            drugs: item?.drugsId?.drugs || "",
            type: item?.drugsId?.type || "",
            duration: item?.duration || "",
            frequency: item?.frequency || "",
            createdAt: item?.createdAt || "",
          }));
        }

        dispatch(addAditionalNote(aditionalNote ?? ""));
        dispatch(addDiagnoseNote(patientComplain ?? ""));
        dispatch(addDiagnoses(diagnose ?? []));
        dispatch(addDiagnosesImage(imageDiagnose ?? []));
        dispatch(addTreatmentImage(imageTreatment ?? []));
        dispatch(addPatientComplains(complain ?? []));
        dispatch(addInvestigations(investigation ?? []));
        dispatch(addIntraOralSoftTissues(intraOralSoftTissues ?? []));
        dispatch(
          setExtraOralExamination(
            extraOralExamination ?? {
              facialAsymmetry: "",
              swellings: "",
              scars: "",
              lymphadenopathy: "",
              other: "",
            }
          )
        );
        dispatch(
          setOcclusalAssessment(
            occlusalAssessment ?? {
              incisorRelationship: "",
              canineRelationshipRight: "",
              canineRelationshipLeft: "",
              molarRelationshipRight: "",
              molarRelationshipLeft: "",
              anteriorGuidance: "",
              lateralGuidanceRight: "",
              lateralGuidanceLeft: "",
            }
          )
        );
        const bopSites = Array.isArray(bopChart) ? bopChart.slice(0, 6) : [];
        dispatch(setBopChart([...bopSites, ...Array(6 - bopSites.length).fill("")].slice(0, 6)));
        dispatch(addPrescriptions(cleanDoseArray(prescription) ?? []));
        dispatch(addAdvancedDiagnose(advancedDiagnose ?? []));
        dispatch(addPastHistories(pastHistory ?? []));
      })
      .catch(console.error)
      .finally(() => dispatch(setloadeDeactive()));
  }, [ID, dispatch]);

  useEffect(() => {
    getData();
  }, [getData, makeAction, makeaction]);

  const handleSave = useCallback(
    (e: any) => {
      dispatch(setloadeActive());
      const imagesData = formData.map((imageData) => imageData.base64);
      baseUrl
        .post(
          "treatment/addNoteToDiagnose",
          {
            patientComplain: teeth.diagnoseNote,
            aditionalNote: teeth.aditionalNote,
            image: imagesData,
            extraOralExamination: teeth.extraOralExamination ?? {
              facialAsymmetry: "",
              swellings: "",
              scars: "",
              lymphadenopathy: "",
              other: "",
            },
            occlusalAssessment: teeth.occlusalAssessment ?? {
              incisorRelationship: "",
              canineRelationshipRight: "",
              canineRelationshipLeft: "",
              molarRelationshipRight: "",
              molarRelationshipLeft: "",
              anteriorGuidance: "",
              lateralGuidanceRight: "",
              lateralGuidanceLeft: "",
            },
            bopChart: teeth.bopChart ?? ["", "", "", "", "", ""],
          },
          {
            headers: { treatmentid: ID },
          }
        )
        .then(() =>
          e
            ? navigate(
              `/client/treatment-diagnose/add-treatment/treatment-table/${ID}`
            )
            : null
        )
        .catch(console.error)
        .finally(() => {
          dispatch(setloadeDeactive());
          toast("Saved");
        });
    },
    [dispatch, formData, ID, navigate, teeth]
  );

  const handleRemove = useCallback(
    (diagnoseImageid: string) => {
      dispatch(setloadeActive());
      baseUrl
        .delete("treatment/remove-removeDiagnoseImage", {
          headers: { treatmentid: ID, diagnoseImageid },
        })
        .then(() => getData())
        .catch(console.error)
        .finally(() => dispatch(setloadeDeactive()));
    },
    [ID, dispatch, getData]
  );

  const handleAditionalNoteChange = (e: any) => {
    dispatch(addAditionalNote(e));
  };


  return (
    <div className="space-y-3">
      {/* Adult/Child Toggle */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-100 dark:border-slate-700 p-3">
        <GenderButton
          onChange={() => setadult(!adult)}
          adult={adult}
          one="Adult"
          two="Child"
        />
      </div>

      <React.Suspense
        fallback={
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        }
      >
        <PastHistory setmakeAction={setmakeAction} makeAction={makeAction} />
        {/* Patient Complain */}
        <PatientComplain
          setmakeAction={setmakeAction}
          makeAction={makeAction}
          adult={adult}
        />

        {/* Investigation */}
        <Investigation
          setmakeAction={setmakeAction}
          makeAction={makeAction}
          adult={adult}
        />


        {!advancedChart ? (
          <>
            <AllDiagnose setdiagnoses={setdiagnoses} />
            {/* Teeth Chart */}
            <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-4 overflow-x-auto">
              {adult ? (
                <Adult
                  diagnoses={diagnoses}
                  adult={adult}
                  setmakeAction={setmakeAction}
                  makeAction={makeAction}
                />
              ) : (
                <Child
                  diagnoses={diagnoses}
                  adult={adult}
                  setmakeAction={setmakeAction}
                  makeAction={makeAction}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <IntraOralSoftTissues
              setmakeAction={setmakeAction}
              makeAction={makeAction}
              adult={adult}
            />
            <ExtraOralExamination />
            <OcclusalAssessment />
            <BopChart />
            <TeethCharts diagnoses={diagnoses} makeAction={makeAction} setmakeAction={setmakeAction} />

          </>
        )}


        {/* Diagnose Form */}
        <DiagnoseForm adult={adult} />
        {/* Diagnose Table */}
        <DiagnoseTable
          setmakeAction={setmakeAction}
          makeAction={makeAction}
          adult={adult}
        />
        {/* Additional Note */}
        <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Additional Note / Diagnosis Statement  
          </label>
          <textarea
            value={teeth.aditionalNote}
            onChange={(e) => handleAditionalNoteChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all resize-none h-16 text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-400"
            placeholder="Enter additional notes..."
          />
        </div>
        {/* X-Ray Images */}
        <AddXray
          formData={formData}
          setFormData={setFormData}
          handleRemove={handleRemove}
          apiImage={teeth.diagnoseImage}
        />
      </React.Suspense>

      {/* Action Buttons */}
      <div className="flex gap-2 justify-end">
        <button
          onClick={() => handleSave(false)}
          className="px-4 py-2 rounded-lg bg-slate-700 dark:bg-slate-800 border border-slate-600 dark:border-slate-600 text-white font-medium text-xs hover:bg-slate-600 dark:hover:bg-slate-700 transition-colors"
        >
          Save
        </button>
        <button
          onClick={() => handleSave(true)}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium text-xs hover:shadow-lg hover:shadow-primary/20 transition-all"
        >
          Go to Treatment
        </button>
      </div>
    </div>
  );
}

export default React.memo(Diagnose);
